import React, { useRef, useEffect, useState, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { handleRelayout } from '../PPMap/utils';
import { VotingMarker } from './VotingMarker';

export function InteractiveMap({
  mapData,
  mapCenter,
  zoomLevel,
  colorBarRange,
  updateMapView,
  mapboxStyle,
  colorscalePalette,
  onMapClick,
  userEmail,
  isAuthenticated,
  onVote
}) {
  const [markerPositions, setMarkerPositions] = useState([]);
  const [isMapMoving, setIsMapMoving] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const plotRef = useRef(null);
  const moveTimeoutRef = useRef(null);

  const handleRightClick = (event) => {
    event.preventDefault();

    if (!onMapClick) return;

    const plotElement = event.currentTarget.querySelector('.js-plotly-plot .plotly');
    if (!plotElement) {
      onMapClick({ points: [] });
      return;
    }

    const rect = plotElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Simple pixel to lat/lng conversion (approximate but works immediately)
    const metersPerPixel = 156543.03392 * Math.cos(mapCenter.lat * Math.PI / 180) / Math.pow(2, zoomLevel);
    const dx = x - rect.width / 2;
    const dy = y - rect.height / 2;
    const dxMeters = dx * metersPerPixel;
    const dyMeters = -dy * metersPerPixel;
    const lat = mapCenter.lat + (dyMeters / 111320);
    const lng = mapCenter.lon + (dxMeters / (111320 * Math.cos(mapCenter.lat * Math.PI / 180)));

    onMapClick({
      points: [],
      latlng: { lat, lng }
    });
  };

  // Calculate pixel positions for markers when map stops moving
  const calculatePositions = useCallback(() => {
    try {
      const plotDiv = plotRef.current?.el;
      if (!plotDiv) {
        return;
      }

      const plotlyMap = plotDiv._fullLayout?.mapbox?._subplot;
      if (!plotlyMap) {
        return;
      }

      const positions = mapData.map((submission) => {
        const point = plotlyMap.project([submission.longitude, submission.latitude]);
        return {
          submission,
          x: point.x,
          y: point.y
        };
      });

      setMarkerPositions(positions);
    } catch (error) {
      console.error('Error calculating marker positions:', error?.message || 'unknown');
    }
  }, [mapData]);

  // Recalculate positions when map data changes or map stops moving
  useEffect(() => {
    if (!isMapMoving && plotRef.current && mapData.length) {
      calculatePositions();
    }
  }, [mapData, isMapMoving, calculatePositions]);

  // Handle map movement detection via relayout events
  const handleMapRelayout = (event) => {
    // Set map as moving and close any open popup
    setIsMapMoving(true);
    setSelectedMarkerId(null);

    // Clear any existing timeout
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current);
    }

    // Set a timeout to detect when map has stopped moving
    moveTimeoutRef.current = setTimeout(() => {
      setIsMapMoving(false);
    }, 150); // 150ms debounce - map is "stopped" after no events for this duration

    // Call the original updateMapView handler
    handleRelayout(updateMapView)(event);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
    };
  }, []);

  const plotData = {
    type: 'scattermapbox',
    mode: 'markers',
    marker: {
      size: 0, // Hide plotly markers, we'll use custom ones
      opacity: 0
    },
    lat: mapData.map(d => d.latitude),
    lon: mapData.map(d => d.longitude),
    hoverinfo: 'skip'
  };

  const plotLayout = {
    autosize: true,
    hovermode: false,
    dragmode: 'pan',
    mapbox: {
      style: mapboxStyle || 'open-street-map',
      center: mapCenter,
      zoom: zoomLevel,
      doubleClickZoom: false, // THIS IS THE KEY ONE
    },
    margin: {
      l: 0,
      r: 0,
      b: 0,
      t: 0,
      pad: 4
    }
  };

  const plotStyle = {
    width: '100%',
    height: '100vh',
  };

  const plotConfig = {
    displayModeBar: false,
    scrollZoom: true,
    doubleClick: false,
    modeBarButtonsToRemove: ['zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d']
  };

  return (
    <div
      style={{ position: 'relative', touchAction: 'none', width: '100%', height: '100vh' }}
      onContextMenu={handleRightClick}
    >
      <Plot
        ref={plotRef}
        data={[plotData]}
        layout={plotLayout}
        style={plotStyle}
        config={plotConfig}
        onRelayout={handleMapRelayout}
      />

      {/* Render custom voting markers only when map is not moving */}
      {!isMapMoving && markerPositions.map(({ submission, x, y }) => (
        <VotingMarker
          key={submission.submission_id}
          submission={submission}
          position={{ x, y }}
          onVote={onVote}
          isAuthenticated={isAuthenticated}
          isSelected={selectedMarkerId === submission.submission_id}
          onSelect={setSelectedMarkerId}
        />
      ))}

      {/* Map instructions */}
      <div className="map-instructions">
        Right-click to submit a transaction
      </div>
    </div>
  );
}
